/**
 * Kickard Game Engine — Pure Reducer (Continuous-Turn variant).
 *
 *   - Drawn card is the actor: it either is PLACED in an empty own slot
 *     or it ATTACKS an opponent card it can defeat.
 *   - After a successful action (place / attack-win / joker-goal) the SAME
 *     player automatically draws again.
 *   - When the drawn card has NO valid action it is auto-discarded and the
 *     turn passes to the opponent.
 *   - Equal-card attacks are NOT exposed as valid targets (the engine pre-
 *     filters), so ties practically never happen during normal play; if they
 *     ever did (forced via dev tooling) the drawn card would still be
 *     discarded and the turn would pass.
 */

import type {
  Action,
  ApplyResult,
  Card,
  GameEvent,
  GameState,
  MatchOptions,
  PlayerId,
  PlayerState,
  SlotType,
} from './types';
import { buildShuffledSingleDeck } from './deck';
import {
  canAttackWithDrawn,
  canPlace,
  compareCards,
  countOccupied,
  hasAnyValidAction,
  hasAnyValidDoubleChanceAction,
  isJoker,
  otherPlayer,
} from './rules';
import { mulberry32, shuffleInPlace } from './rng';
import { buildPenaltyStateFromRemaining, applyPenaltyAction } from './penalty';

function emptyPlayer(id: PlayerId, name: string, isBot: boolean): PlayerState {
  return {
    id,
    name,
    isBot,
    attacks: [null, null, null],
    defenses: [null, null],
    goalkeeper: null,
    score: 0,
    attacksPerformed: 0,
    savesPerformed: 0,
  };
}

/**
 * Deal the 12-card starting formation from `deck` (in place) into `p1` and `p2`.
 *
 * Joker V2 rule (2026-02-12 part 8): Jokers ARE allowed in starting slots —
 * any joker dealt is flagged isDoubleChance=true and lives on the board as a
 * Double-Chance card from turn 1. Same applies whether dealt to attack,
 * defense or goalkeeper. GK hiding is the same as for any other card.
 *
 *   1. p1 GK · 2. p2 GK
 *   3-4. p1 Def · 5-6. p2 Def
 *   7-9. p1 Atk · 10-12. p2 Atk
 */
function dealFormation(
  deck: Card[],
  p1: PlayerState,
  p2: PlayerState,
  _setAside: Card[],
): void {
  const dealNext = (): Card => {
    if (deck.length === 0) {
      throw new Error('Kickard: deck exhausted before starting formation completed.');
    }
    const c = deck.shift() as Card;
    if (isJoker(c)) return { ...c, isDoubleChance: true };
    return c;
  };

  p1.goalkeeper = dealNext();
  p2.goalkeeper = dealNext();
  p1.defenses[0] = dealNext();
  p1.defenses[1] = dealNext();
  p2.defenses[0] = dealNext();
  p2.defenses[1] = dealNext();
  p1.attacks[0] = dealNext();
  p1.attacks[1] = dealNext();
  p1.attacks[2] = dealNext();
  p2.attacks[0] = dealNext();
  p2.attacks[1] = dealNext();
  p2.attacks[2] = dealNext();
}

/**
 * Initial state for a new match.
 *
 * Match format = TWO HALVES. Each half uses a fresh single deck (54 cards =
 * 52 + 2 Jokers). After dealing the 12-card formation, 42 cards remain in the
 * half's deck. Scores carry between halves; formations reset; second-half
 * starter is the player who did NOT start the first half.
 *
 * Jokers are NOT allowed in starting positions — they are set aside during the
 * deal and returned to the remaining deck (which is then re-shuffled).
 */
export function newMatch(opts: MatchOptions = {}): GameState {
  const seed = opts.seed ?? Math.floor(Math.random() * 0xFFFFFFFF);
  const deck = buildShuffledSingleDeck(seed, `h1-${seed}`);
  const matchId = `m-${seed}-${Date.now()}`;
  const p1 = emptyPlayer('p1', opts.p1Name ?? 'You', false);
  const p2 = emptyPlayer('p2', opts.p2Name ?? 'Bot', opts.p2IsBot ?? true);

  const setAside: Card[] = [];
  dealFormation(deck, p1, p2, setAside);

  // setAside is no longer used (jokers are dealt in place). The remaining
  // deck is what's left; we still reshuffle once to scramble the per-half
  // RNG sub-stream so successive deals don't share patterns.
  shuffleInPlace(deck, mulberry32(seed + 1));

  return {
    matchId,
    seed,
    half: 1,
    firstHalfStarter: 'p1',
    deck,
    discard: [],
    players: { p1, p2 },
    currentPlayer: 'p1',
    phase: 'turn-start',
    drawnCard: null,
    lastDrawnBy: null,
    turnNumber: 1,
    tick: 0,
  };
}

function cloneState(s: GameState): GameState {
  return JSON.parse(JSON.stringify(s));
}

/**
 * When the half's deck runs out:
 *   - If we are still in the first half → enter 'halftime'. The UI will show
 *     scores and dispatch START_SECOND_HALF to continue.
 *   - If we are in the second half      → resolve match end (or penalties on
 *     a tied score).
 */
function endHalfOrMatch(state: GameState, events: GameEvent[]) {
  if (state.half === 1) {
    state.drawnCard = null;
    state.phase = 'halftime';
    events.push({
      type: 'HALFTIME',
      scoreP1: state.players.p1.score,
      scoreP2: state.players.p2.score,
    });
    return;
  }
  // Second half ended — final result.
  const s1 = state.players.p1.score;
  const s2 = state.players.p2.score;
  state.phase = 'match-end';
  if (s1 > s2) {
    state.endState = { winner: 'p1', reason: 'more-goals' };
    events.push({ type: 'MATCH_END', winner: 'p1' });
  } else if (s2 > s1) {
    state.endState = { winner: 'p2', reason: 'more-goals' };
    events.push({ type: 'MATCH_END', winner: 'p2' });
  } else {
    state.penalty = buildPenaltyStateFromRemaining(state);
    state.phase = 'penalty';
  }
}

/**
 * Start the second half — fresh deck, fresh formations, alternating starter.
 * Score carries over.
 */
function startSecondHalf(state: GameState, events: GameEvent[]) {
  const seed2 = state.seed + 1234567;
  const deck = buildShuffledSingleDeck(seed2, `h2-${state.seed}`);

  // Reset board for both players, preserve scores + counters.
  for (const id of ['p1', 'p2'] as PlayerId[]) {
    state.players[id].attacks = [null, null, null];
    state.players[id].defenses = [null, null];
    state.players[id].goalkeeper = null;
  }

  const setAside: Card[] = [];
  dealFormation(deck, state.players.p1, state.players.p2, setAside);

  shuffleInPlace(deck, mulberry32(seed2 + 1));

  state.deck = deck;
  state.discard = [];
  state.half = 2;
  state.drawnCard = null;
  state.lastDrawnBy = null;
  state.phase = 'turn-start';
  state.turnNumber += 1;
  state.currentPlayer = otherPlayer(state.firstHalfStarter);
  events.push({ type: 'SECOND_HALF_STARTED', starter: state.currentPlayer });
}

/** Same player continues — reset to turn-start. Half (or match) ends if deck is empty. */
function continueTurn(state: GameState, events: GameEvent[]) {
  state.phase = 'turn-start';
  state.drawnCard = null;
  state.turnNumber += 1;
  if (state.deck.length === 0) endHalfOrMatch(state, events);
}

/** Pass turn to the opponent. */
function passTurn(state: GameState, events: GameEvent[], reason: 'no-move' | 'goal' | 'save' | 'tie' | 'timeout' | 'manual' = 'no-move') {
  if (state.deck.length === 0) {
    state.drawnCard = null;
    endHalfOrMatch(state, events);
    return;
  }
  const from = state.currentPlayer;
  const to = otherPlayer(state.currentPlayer);
  state.currentPlayer = to;
  state.phase = 'turn-start';
  state.drawnCard = null;
  state.turnNumber += 1;
  events.push({ type: 'TURN_PASSED', from, to, reason });
}

/**
 * Pop a single card from the deck for an inline comparison (used by Joker V2
 * Double-Chance attack/defense). Returns null if the deck is empty.
 */
function popOne(state: GameState): Card | null {
  if (state.deck.length === 0) return null;
  return state.deck.shift() as Card;
}

/**
 * Joker V2 — Double Chance attack on an opponent's attack/defense card.
 *
 * Lazy second-draw rule (2026-02-12 part 8):
 *   1. Draw card #1 from the shared deck. Compare to target.
 *      • If it defeats the target → SUCCESS, target removed, STOP (no 2nd draw).
 *   2. Otherwise draw card #2. Compare.
 *      • If it defeats the target → SUCCESS, target removed.
 *      • Else → target survives.
 *
 *   Drawn comparison cards are pushed to discard. The Joker itself is also
 *   discarded. The turn CONTINUES.
 */
function resolveDoubleChanceAttack(
  state: GameState,
  events: GameEvent[],
  target: { player: PlayerId; slotType: 'attack' | 'defense'; slotIndex: number },
) {
  const me = state.players[state.currentPlayer];
  const opp = state.players[target.player];
  const slotArr = target.slotType === 'attack' ? opp.attacks : opp.defenses;
  const targetCard = slotArr[target.slotIndex];
  if (!targetCard) return;

  // DC vs DC special resolution (2026-02-12 part 10): both sides keep their
  // double-chance power. Two pair-wise rounds: attacker.atk vs defender.def.
  if (targetCard.isDoubleChance) {
    return resolveDcVsDc(state, events, target, targetCard);
  }

  const joker = state.drawnCard as Card;
  state.discard.push(joker);
  state.drawnCard = null;

  // Phase 3.8.1 — Joker-inside-DC rule: if either of the 2 base draws is a
  // joker, that side gets 2 ADDITIONAL drawn cards. Keep the existing lazy
  // first-draw success rule when no joker appears (preserves no-regression).
  const attackerDraws: Card[] = [];
  let success = false;
  const evalCard = (c: Card) => {
    if (!isJoker(c) && compareCards(c, targetCard) > 0) success = true;
  };
  const draw = () => {
    const c = popOne(state);
    if (!c) return null;
    attackerDraws.push(c);
    state.discard.push(c);
    return c;
  };

  const c1 = draw();
  if (c1) {
    if (isJoker(c1)) {
      drawDcExtras(state, attackerDraws);
    } else {
      evalCard(c1);
    }
  }
  // 2nd base draw — only if we haven't already succeeded with a non-joker first.
  if (!success && attackerDraws.length < 2) {
    const c2 = draw();
    if (c2) {
      if (isJoker(c2)) {
        drawDcExtras(state, attackerDraws);
      } else {
        evalCard(c2);
      }
    }
  }
  // If joker expansion happened, re-evaluate the entire pool.
  if (!success) {
    for (const c of attackerDraws) {
      if (!isJoker(c) && compareCards(c, targetCard) > 0) { success = true; break; }
    }
  }

  if (success) {
    state.discard.push(targetCard);
    slotArr[target.slotIndex] = null;
    me.attacksPerformed += 1;
  }

  events.push({
    type: 'JOKER_DOUBLE_ATTACK_RESULT',
    player: me.id,
    jokerCard: joker,
    target,
    targetCard,
    draws: attackerDraws,
    attackerDraws,
    success,
  });

  // Turn-flow rule (2026-02-13): a Double Chance attack that FAILS to remove
  // its target is a momentum-breaking event — initiative passes to defender.
  // Successful DC attack → attacker keeps initiative (continueTurn).
  if (success) {
    continueTurn(state, events);
  } else {
    passTurn(state, events, 'save');
  }
}

/**
 * Phase 3.8.1 — Joker-inside-DC helper.
 * Grants 2 additional drawn cards to a side because a joker was drawn during
 * Double Chance resolution. Recursive: if those extras are also jokers, they
 * grant 2 more each. Capped by deck exhaustion (popOne returns null).
 * All revealed cards are pushed to `outDraws` AND to state.discard.
 */
function drawDcExtras(state: GameState, outDraws: Card[]): void {
  let toDraw = 2;
  while (toDraw > 0) {
    const c = popOne(state);
    if (!c) break;
    outDraws.push(c);
    state.discard.push(c);
    toDraw -= 1;
    if (isJoker(c)) toDraw += 2;
  }
}

/**
 * DC-vs-DC (2026-02-12 part 10):
 *   Round 1 — atkDraw1 vs defDraw1. Attacker wins (strictly) → success.
 *   Round 2 (only if round 1 not won) — atkDraw2 vs defDraw2.
 *     Attacker wins strictly → success. Else defender blocks → turn passes.
 *
 *   Equal cards = defender wins (per spec: "If defender wins OR draws,
 *   attacker may use second chance"; round 2 same rule). All drawn cards go
 *   to discard. The merged interleaved draw list goes into the event so the
 *   UI can render the matchup.
 */
function resolveDcVsDc(
  state: GameState,
  events: GameEvent[],
  target: { player: PlayerId; slotType: 'attack' | 'defense'; slotIndex: number },
  targetCard: Card,
) {
  const me = state.players[state.currentPlayer];
  const opp = state.players[target.player];
  const slotArr = target.slotType === 'attack' ? opp.attacks : opp.defenses;

  const joker = state.drawnCard as Card;
  state.discard.push(joker);
  state.drawnCard = null;

  // Phase 3.8.1 — Two-sided draws + Joker-inside-DC expansion.
  // Both sides ALWAYS draw 2 base cards. Any joker in a side's draws grants
  // that side 2 extra cards (recursive). The merged interleaved order in
  // `draws` is preserved for backwards compatibility, but the UI now reads
  // `attackerDraws` / `defenderDraws` to render two clearly separated sides.
  const attackerDraws: Card[] = [];
  const defenderDraws: Card[] = [];
  const merged: Card[] = [];

  const drawInto = (side: Card[]) => {
    const c = popOne(state);
    if (!c) return null;
    side.push(c);
    merged.push(c);
    state.discard.push(c);
    if (isJoker(c)) drawDcExtras(state, side);
    return c;
  };

  // Round 1 — atk1 then def1 (interleaved for the legacy `draws` list).
  drawInto(attackerDraws);
  drawInto(defenderDraws);
  // Round 2 — atk2 then def2.
  drawInto(attackerDraws);
  drawInto(defenderDraws);

  // Success = attacker's best non-joker > defender's best non-joker
  // (strict win required, ties favour defender).
  const atkBest = bestNonJoker(attackerDraws);
  const defBest = bestNonJoker(defenderDraws);
  let success = false;
  if (atkBest && (!defBest || compareCards(atkBest, defBest) > 0)) success = true;

  if (success) {
    state.discard.push(targetCard);
    slotArr[target.slotIndex] = null;
    me.attacksPerformed += 1;
  }

  events.push({
    type: 'JOKER_DOUBLE_ATTACK_RESULT',
    player: me.id,
    jokerCard: joker,
    target,
    targetCard,
    draws: merged,
    attackerDraws,
    defenderDraws,
    success,
  });

  if (success) {
    continueTurn(state, events);
  } else {
    passTurn(state, events, 'save');
  }
}

/** Highest non-joker card in a pool, or null if all jokers / empty. */
function bestNonJoker(cards: Card[]): Card | null {
  let best: Card | null = null;
  for (const c of cards) {
    if (isJoker(c)) continue;
    if (best === null || compareCards(c, best) > 0) best = c;
  }
  return best;
}

/**
 * Joker V2 — Defender uses defensive draws to block an attack on their placed
 * Double Chance card (attack/defense slots — see resolveDoubleChanceGkDefense
 * for the GK-slot variant).
 *
 * Lazy second-draw rule:
 *   1. Defender draws card #1. Compare as DEFENSE vs attacker.
 *      • If it defeats attacker → BLOCKED, DC stays, STOP.
 *   2. Otherwise draw card #2.
 *      • If it defeats attacker → BLOCKED.
 *      • Else → DC removed.
 */
function resolveDoubleChanceDefense(
  state: GameState,
  events: GameEvent[],
  attacker: Card,
  target: { player: PlayerId; slotType: 'attack' | 'defense'; slotIndex: number },
) {
  const me = state.players[state.currentPlayer];
  const opp = state.players[target.player];
  const slotArr = target.slotType === 'attack' ? opp.attacks : opp.defenses;
  const dcCard = slotArr[target.slotIndex];
  if (!dcCard) return;

  state.discard.push(attacker);
  state.drawnCard = null;
  me.attacksPerformed += 1;

  // Phase 3.8.1 — Joker-inside-DC: if a defensive draw is a joker, the
  // defender gets 2 extra cards. Lazy first-draw success preserved when no
  // joker appears (no regression on existing tests).
  const draws: Card[] = [];
  let blocked = false;
  const evalCard = (c: Card) => {
    // Tie counts as a successful block (2026-02-12 part 10).
    if (!isJoker(c) && compareCards(c, attacker) >= 0) blocked = true;
  };
  const draw = () => {
    const c = popOne(state);
    if (!c) return null;
    draws.push(c);
    state.discard.push(c);
    return c;
  };

  const c1 = draw();
  if (c1) {
    if (isJoker(c1)) drawDcExtras(state, draws);
    else evalCard(c1);
  }
  if (!blocked && draws.length < 2) {
    const c2 = draw();
    if (c2) {
      if (isJoker(c2)) drawDcExtras(state, draws);
      else evalCard(c2);
    }
  }
  // After possible joker expansion, evaluate the whole pool.
  if (!blocked) {
    for (const c of draws) {
      if (!isJoker(c) && compareCards(c, attacker) >= 0) { blocked = true; break; }
    }
  }

  if (!blocked) {
    state.discard.push(dcCard);
    slotArr[target.slotIndex] = null;
  }

  events.push({
    type: 'JOKER_DC_DEFENSE_RESULT',
    attacker,
    defenderId: opp.id,
    targetSlot: { slotType: target.slotType, slotIndex: target.slotIndex },
    dcCard,
    draws,
    blocked,
  });

  // Turn-flow rule (2026-02-12 part 9):
  //   • BLOCKED — successful DC defense is a momentum-breaking event. The
  //     attacker LOSES initiative and the defender immediately gains the turn.
  //   • NOT BLOCKED — DC was overrun. Attacker keeps initiative (same as a
  //     normal attack-line win — continueTurn).
  if (blocked) {
    passTurn(state, events, 'save');
  } else {
    continueTurn(state, events);
  }
}

/**
 * Joker V2 — Goalkeeper variant of Double Chance defense (2026-02-12 part 8).
 *
 *   • Attacker fires their drawn card at a DC goalkeeper.
 *   • GK gets up to TWO defensive draws (lazy — only 2nd if 1st fails).
 *   • If EITHER draw defeats attacker → SAVE.
 *   • If BOTH fail → GOAL.
 *   • GK is ALWAYS discarded after the shot (normal GK rule applies — outcome
 *     does not change this). Turn passes to opponent regardless.
 *
 *   We emit GOAL/SAVE with `dcDraws` so the UI's ShotSequence can render the
 *   special two-draw GK reveal.
 */
function resolveDoubleChanceGkShot(
  state: GameState,
  events: GameEvent[],
  shooter: Card,
  gk: Card,
) {
  const me = state.players[state.currentPlayer];
  const oppId: PlayerId = me.id === 'p1' ? 'p2' : 'p1';
  const opp = state.players[oppId];

  // Discard the attacker's drawn card; GK fate (kept vs discarded) decided
  // by outcome (2026-02-12 part 10: DC GK is NOT discarded on a save).
  state.discard.push(shooter);
  state.drawnCard = null;

  // Phase 3.8.1 — Joker-inside-DC: if a defensive draw is a joker, the DC
  // GK side gets 2 extra cards. Lazy first-draw save preserved when no
  // joker appears.
  const draws: Card[] = [];
  let blocked = false;
  const evalCard = (c: Card) => {
    if (!isJoker(c) && compareCards(c, shooter) >= 0) blocked = true;
  };
  const draw = () => {
    const c = popOne(state);
    if (!c) return null;
    draws.push(c);
    state.discard.push(c);
    return c;
  };

  const c1 = draw();
  if (c1) {
    if (isJoker(c1)) drawDcExtras(state, draws);
    else evalCard(c1);
  }
  if (!blocked && draws.length < 2) {
    const c2 = draw();
    if (c2) {
      if (isJoker(c2)) drawDcExtras(state, draws);
      else evalCard(c2);
    }
  }
  if (!blocked) {
    for (const c of draws) {
      if (!isJoker(c) && compareCards(c, shooter) >= 0) { blocked = true; break; }
    }
  }

  if (blocked) {
    // SAVE — DC GK survives the shot, unlike a normal GK.
    opp.savesPerformed += 1;
    events.push({ type: 'SAVE', defender: opp.id, shooter, gk, dcDraws: draws });
    passTurn(state, events, 'save');
  } else {
    // GOAL — DC GK is overrun and discarded (normal GK-removal applies here).
    state.discard.push(gk);
    opp.goalkeeper = null;
    me.score += 1;
    events.push({ type: 'GOAL', scorer: me.id, shooter, gk, dcDraws: draws });
    passTurn(state, events, 'goal');
  }
}



/**
 * Joker shot — drawn joker is discarded, next NON-JOKER card shoots directly
 * at opp GK.
 *
 * BUG FIX #2 (2026-02-14, production): joker-on-joker follow-up MUST NOT be
 * treated as an auto-goal. Any joker(s) drawn as follow-up are discarded
 * as "skipped" and we keep drawing until a valid non-joker follow-up is
 * found. If the deck has no non-joker card left, the shot cannot be
 * resolved — safe fallback: no shot, turn passes as 'no-move' (same as
 * the existing empty-deck fallback at the entry of this function).
 *
 * After a valid follow-up is found:
 *   - goal → turn passes to opponent (goal always ends the active turn).
 *   - save → turn passes to opponent.
 */
function resolveJokerShot(state: GameState, jokerCard: Card, events: GameEvent[]) {
  state.discard.push(jokerCard);
  if (state.deck.length === 0) {
    passTurn(state, events, 'no-move');
    return;
  }

  // Draw follow-up card(s) — skip any joker(s) until we find a non-joker.
  const skippedFollowUps: Card[] = [];
  let followUp: Card | null = null;
  while (state.deck.length > 0) {
    const next = state.deck.shift() as Card;
    if (isJoker(next)) {
      // Skip this joker as a follow-up: it is NOT the shot card and NEVER
      // auto-scores. Discard it (same as a normal drawn+discarded card) and
      // continue searching for the first non-joker.
      state.discard.push(next);
      skippedFollowUps.push(next);
      continue;
    }
    followUp = next;
    break;
  }

  if (!followUp) {
    // Safe fallback — deck exhausted before finding a non-joker. The shot
    // cannot be resolved; treat as no-move (same shape as the empty-deck
    // guard above). Skipped jokers remain discarded (they were already
    // pushed to state.discard during the draw loop).
    passTurn(state, events, 'no-move');
    return;
  }

  const me = state.players[state.currentPlayer];
  const opp = state.players[otherPlayer(state.currentPlayer)];

  // Emit the JOKER event with the RESOLVED (non-joker) follow-up card so
  // the UI's ShotSequence renders the correct card, and any skipped
  // jokers travel along for optional UI/log rendering. Existing consumers
  // that ignore `skippedFollowUps` are unaffected.
  events.push({
    type: 'JOKER',
    player: state.currentPlayer,
    jokerCard,
    followUp,
    ...(skippedFollowUps.length > 0 ? { skippedFollowUps } : {}),
  });

  if (!opp.goalkeeper) {
    // No GK → auto goal (empty net rule).
    state.discard.push(followUp);
    me.score += 1;
    me.attacksPerformed += 1;
    events.push({ type: 'GOAL', scorer: me.id, shooter: followUp, gk: null });
    passTurn(state, events, 'goal');
    return;
  }
  const gk = opp.goalkeeper;
  // BUG FIX #1 (2026-02-13): a Joker SHOOT against a Double Chance Goalkeeper
  // MUST trigger the DC GK defense path (up to 2 defensive draws, DC GK
  // survives a SAVE). Previously this branch did a plain compareCards and
  // discarded the DC GK unconditionally — breaking the DC GK invariants and
  // skipping the visual draw sequence.
  if (isJoker(gk)) {
    resolveDoubleChanceGkShot(state, events, followUp, gk);
    return;
  }
  const cmp = compareCards(followUp, gk);
  state.discard.push(followUp);
  state.discard.push(gk);
  opp.goalkeeper = null;
  me.attacksPerformed += 1;
  if (cmp > 0) {
    me.score += 1;
    events.push({ type: 'GOAL', scorer: me.id, shooter: followUp, gk });
    passTurn(state, events, 'goal');
  } else {
    opp.savesPerformed += 1;
    events.push({ type: 'SAVE', defender: opp.id, shooter: followUp, gk });
    passTurn(state, events, 'save');
  }
}

export function applyAction(prev: GameState, action: Action): ApplyResult {
  const state = cloneState(prev);
  state.tick += 1;
  const events: GameEvent[] = [];

  if (state.phase === 'penalty' && state.penalty) {
    applyPenaltyAction(state, action, events);
    return { state, events };
  }

  // ----- DEV ACTIONS -----
  if (action.type === 'DEV_RESTART') {
    return { state: newMatch({ seed: state.seed + 1, p2IsBot: state.players.p2.isBot }), events };
  }
  if (action.type === 'DEV_FORCE_PENALTY') {
    state.endState = undefined;
    state.penalty = buildPenaltyStateFromRemaining(state);
    state.phase = 'penalty';
    return { state, events };
  }
  if (action.type === 'DEV_FORCE_HALFTIME') {
    // Drain current-half deck to force the halftime transition.
    state.deck = [];
    state.drawnCard = null;
    endHalfOrMatch(state, events);
    return { state, events };
  }
  if (action.type === 'DEV_SPAWN_JOKER') {
    const joker: Card = { id: `dev-joker-${state.tick}`, suit: 'joker', rank: 'JOKER', value: 0 };
    state.deck.unshift(joker);
    return { state, events };
  }
  if (action.type === 'DEV_REVEAL_ALL') {
    return { state, events };
  }

  if (state.phase === 'match-end' || state.phase === 'finished') {
    return { state, events };
  }

  // START_SECOND_HALF is valid only from halftime; resets formations + deck.
  if (action.type === 'START_SECOND_HALF') {
    if (state.phase !== 'halftime') return { state: prev, events };
    startSecondHalf(state, events);
    return { state, events };
  }

  if (state.phase === 'halftime') {
    // No other actions are valid during halftime.
    return { state: prev, events };
  }

  switch (action.type) {
    case 'DRAW': {
      if (state.phase !== 'turn-start') return { state: prev, events };
      if (state.deck.length === 0) {
        endHalfOrMatch(state, events);
        return { state, events };
      }
      const card = state.deck.shift() as Card;
      state.lastDrawnBy = state.currentPlayer;
      events.push({ type: 'CARD_DRAWN', player: state.currentPlayer, card });

      if (isJoker(card)) {
        // Joker V2 — do NOT auto-resolve. Enter joker-choice phase so the
        // player (or bot AI) can choose between SHOOT and DOUBLE CHANCE.
        state.drawnCard = card;
        state.phase = 'joker-choice';
        events.push({ type: 'JOKER_CHOICE_OPENED', player: state.currentPlayer, jokerCard: card });
        return { state, events };
      }

      state.drawnCard = card;
      state.phase = 'drawn';
      // NOTE: We intentionally DO NOT auto-discard here even when the drawn card
      // has no valid action. The UI shows the card for ~2 s, then dispatches
      // END_TURN. The same rule applies to the bot. This keeps the drawn card
      // always visible to the player.
      return { state, events };
    }

    case 'PLACE': {
      if (state.phase !== 'drawn' || !state.drawnCard) return { state: prev, events };
      const player = state.players[state.currentPlayer];
      if (!canPlace(player, action.slotType, action.slotIndex, state.drawnCard)) {
        return { state: prev, events };
      }
      const card = state.drawnCard;
      if (action.slotType === 'attack') player.attacks[action.slotIndex] = card;
      else if (action.slotType === 'defense') player.defenses[action.slotIndex] = card;
      else if (action.slotType === 'goalkeeper') player.goalkeeper = card;
      events.push({ type: 'CARD_PLACED', player: player.id, slot: action.slotType, index: action.slotIndex, card });
      // Successful action — same player draws again
      continueTurn(state, events);
      return { state, events };
    }

    case 'ATTACK': {
      if (state.phase !== 'drawn' || !state.drawnCard) return { state: prev, events };
      if (!canAttackWithDrawn(state, action.target)) return { state: prev, events };

      const drawn = state.drawnCard;
      const me = state.players[state.currentPlayer];
      const opp = state.players[action.target.player];
      let targetCard: Card | null = null;
      if (action.target.slotType === 'attack') targetCard = opp.attacks[action.target.slotIndex];
      else if (action.target.slotType === 'defense') targetCard = opp.defenses[action.target.slotIndex];
      else targetCard = opp.goalkeeper;

      // Joker V2 — attacking a placed Double Chance card uses the special
      // two-defensive-draw resolution instead of normal compareCards().
      if (targetCard && (action.target.slotType === 'attack' || action.target.slotType === 'defense')
          && targetCard.isDoubleChance) {
        resolveDoubleChanceDefense(state, events, drawn, {
          player: action.target.player,
          slotType: action.target.slotType,
          slotIndex: action.target.slotIndex,
        });
        return { state, events };
      }

      if (action.target.slotType === 'goalkeeper') {
        // GK attack — always allowed when progression met. GK is ALWAYS
        // discarded after the shot regardless of outcome (normal GK rule).
        // If the GK is a Double-Chance card, defense uses lazy 1-2 draws
        // (see resolveDoubleChanceGkShot); otherwise the normal compare runs.
        if (targetCard && targetCard.isDoubleChance) {
          me.attacksPerformed += 1;
          resolveDoubleChanceGkShot(state, events, drawn, targetCard);
          return { state, events };
        }
        me.attacksPerformed += 1;
        state.discard.push(drawn);
        let isGoal: boolean;
        if (!targetCard) {
          isGoal = true; // empty goal
        } else {
          const cmp = compareCards(drawn, targetCard);
          state.discard.push(targetCard);
          opp.goalkeeper = null;
          isGoal = cmp > 0;
        }
        if (isGoal) {
          me.score += 1;
          events.push({ type: 'GOAL', scorer: me.id, shooter: drawn, gk: targetCard });
        } else {
          opp.savesPerformed += 1;
          events.push({ type: 'SAVE', defender: opp.id, shooter: drawn, gk: targetCard as Card });
        }
        state.drawnCard = null;
        passTurn(state, events, isGoal ? 'goal' : 'save');
      } else {
        // Normal attack on attack/defense — drawn always wins (validation guarantees defeat).
        state.discard.push(targetCard as Card);
        state.discard.push(drawn);
        if (action.target.slotType === 'attack') opp.attacks[action.target.slotIndex] = null;
        else if (action.target.slotType === 'defense') opp.defenses[action.target.slotIndex] = null;
        events.push({
          type: 'ATTACK_RESULT',
          attacker: drawn,
          defender: targetCard as Card,
          outcome: 'win',
          from: me.id,
          to: action.target,
        });
        // Successful non-GK action — same player draws again
        continueTurn(state, events);
      }
      return { state, events };
    }

    case 'JOKER_SHOOT': {
      // Player chose SHOOT during the joker-choice. Reuses the legacy
      // joker-shot resolution: discard joker, next card flies at the GK.
      if (state.phase !== 'joker-choice' || !state.drawnCard) return { state: prev, events };
      const joker = state.drawnCard;
      state.drawnCard = null;
      resolveJokerShot(state, joker, events);
      return { state, events };
    }

    case 'JOKER_DOUBLE_CHOOSE': {
      // Player chose DOUBLE CHANCE. Joker stays in drawnCard. Player now picks
      // ATTACK (non-GK target) or PLACE (atk/def slot) — both follow-up actions
      // are dispatched separately and consume the joker.
      //
      // Soft-lock guard (2026-02-13): if no legal DC action exists (no empty
      // own slot AND no opp atk/def card), auto-fallback to SHOOT to avoid a
      // match-breaking dead end.
      if (state.phase !== 'joker-choice' || !state.drawnCard) return { state: prev, events };
      if (!hasAnyValidDoubleChanceAction(state)) {
        const joker = state.drawnCard;
        state.drawnCard = null;
        events.push({ type: 'JOKER_DC_FALLBACK_SHOOT', player: state.currentPlayer, jokerCard: joker });
        resolveJokerShot(state, joker, events);
        return { state, events };
      }
      state.phase = 'joker-double-action';
      return { state, events };
    }

    case 'JOKER_DOUBLE_ATTACK': {
      if (state.phase !== 'joker-double-action' || !state.drawnCard) return { state: prev, events };
      if (action.target.player === state.currentPlayer) return { state: prev, events };
      if (action.target.slotType !== 'attack' && action.target.slotType !== 'defense') {
        return { state: prev, events };
      }
      const opp = state.players[action.target.player];
      const slotArr = action.target.slotType === 'attack' ? opp.attacks : opp.defenses;
      const tCard = slotArr[action.target.slotIndex];
      if (!tCard) return { state: prev, events };
      resolveDoubleChanceAttack(state, events, action.target);
      return { state, events };
    }

    case 'JOKER_DOUBLE_PLACE': {
      if (state.phase !== 'joker-double-action' || !state.drawnCard) return { state: prev, events };
      const me = state.players[state.currentPlayer];
      if (action.slotType === 'goalkeeper') {
        if (me.goalkeeper !== null) return { state: prev, events };
        const placed: Card = { ...state.drawnCard, isDoubleChance: true };
        me.goalkeeper = placed;
        state.drawnCard = null;
        events.push({ type: 'JOKER_DOUBLE_PLACED', player: me.id, slot: 'goalkeeper', index: 0 });
        events.push({ type: 'CARD_PLACED', player: me.id, slot: 'goalkeeper', index: 0, card: placed });
        continueTurn(state, events);
        return { state, events };
      }
      if (action.slotType !== 'attack' && action.slotType !== 'defense') return { state: prev, events };
      const arr = action.slotType === 'attack' ? me.attacks : me.defenses;
      const maxIdx = action.slotType === 'attack' ? 3 : 2;
      if (action.slotIndex < 0 || action.slotIndex >= maxIdx) return { state: prev, events };
      if (arr[action.slotIndex] !== null) return { state: prev, events };

      const placed: Card = { ...state.drawnCard, isDoubleChance: true };
      arr[action.slotIndex] = placed;
      state.drawnCard = null;
      events.push({ type: 'JOKER_DOUBLE_PLACED', player: me.id, slot: action.slotType, index: action.slotIndex });
      events.push({ type: 'CARD_PLACED', player: me.id, slot: action.slotType, index: action.slotIndex, card: placed });
      continueTurn(state, events);
      return { state, events };
    }

    case 'END_TURN': {
      if (state.phase === 'joker-choice' || state.phase === 'joker-double-action') {
        // Discard the joker, then pass turn (used as a safety / dev escape).
        if (state.drawnCard) {
          state.discard.push(state.drawnCard);
          events.push({ type: 'DRAW_DISCARDED', player: state.currentPlayer, card: state.drawnCard });
          state.drawnCard = null;
        }
        passTurn(state, events, 'no-move');
        return { state, events };
      }
      if (state.drawnCard) {
        state.discard.push(state.drawnCard);
        events.push({ type: 'DRAW_DISCARDED', player: state.currentPlayer, card: state.drawnCard });
        state.drawnCard = null;
      }
      passTurn(state, events, 'manual');
      return { state, events };
    }

    case 'TIMEOUT': {
      if (state.drawnCard) {
        state.discard.push(state.drawnCard);
        events.push({ type: 'DRAW_DISCARDED', player: state.currentPlayer, card: state.drawnCard });
        state.drawnCard = null;
      }
      events.push({ type: 'TIMEOUT', player: state.currentPlayer });
      passTurn(state, events, 'timeout');
      return { state, events };
    }

    default:
      return { state: prev, events };
  }
}

export const selectors = {
  remainingCards: (s: GameState) => s.deck.length,
  totalCards: (s: GameState) =>
    s.deck.length + s.discard.length +
    countOccupied(s.players.p1.attacks) + countOccupied(s.players.p1.defenses) + (s.players.p1.goalkeeper ? 1 : 0) +
    countOccupied(s.players.p2.attacks) + countOccupied(s.players.p2.defenses) + (s.players.p2.goalkeeper ? 1 : 0) +
    (s.drawnCard ? 1 : 0),
  /** Half-deck total (54 cards) minus the 12-card starting formation = 42. */
  halfDeckMaxRemaining: () => 42,
  isMatchOver: (s: GameState) => s.phase === 'match-end' || s.phase === 'finished',
};

export { mulberry32 } from './rng';
